import React, { useState, useEffect } from "react";
import { message } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { apiService, Assessment } from "../services/apiService";
import { useTerminology } from "../hooks/useTerminology";
import {
  CMDA_CONTROL_QUESTIONS,
  CMDA_MONITORING_QUESTIONS,
  CMA_ASSESSMENT_QUESTIONS,
} from "../data/questionnaire-data";
import { adaptQuestion } from "../utils/adaptQuestions";
import "./AssessmentStyles.css";

const ALL_QUESTIONS = [
  ...CMDA_CONTROL_QUESTIONS.questions,
  ...CMDA_MONITORING_QUESTIONS.questions,
  ...CMA_ASSESSMENT_QUESTIONS.control,
  ...CMA_ASSESSMENT_QUESTIONS.monitoring,
];

const AssessmentDetail: React.FC = () => {
  const [assessment, setAssessment] = useState<Assessment | null>(null);
  const [loading, setLoading] = useState(true);
  const [tenantUsers, setTenantUsers] = useState<any[]>([]);
  const navigate = useNavigate();
  const { assessmentId } = useParams<{ assessmentId: string }>();
  const { isAssessor } = useAuth();
  const t = useTerminology();

  useEffect(() => {
    if (assessmentId) {
      console.log("Loading assessment:", assessmentId);
      loadTenantUsers();
      loadAssessment();
    }
  }, [assessmentId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTenantUsers = async () => {
    try {
      const users = await apiService.getUsers();
      setTenantUsers(users);
    } catch (error) {
      console.error("Error loading tenant users:", error);
    }
  };

  const loadAssessment = async () => {
    if (!assessmentId) {
      console.warn("No assessmentId provided");
      return;
    }

    try {
      console.log("Calling API for assessment:", assessmentId);
      setLoading(true);
      const data = await apiService.getAssessment(assessmentId);
      console.log("Assessment loaded:", data);
      console.log("Assessment type:", data.assessmentType);
      console.log("Control responses:", data.controlResponses);
      console.log("Monitoring responses:", data.monitoringResponses);
      setAssessment(data);
    } catch (error: any) {
      console.error("Error loading assessment:", error);
      if (error.response?.status === 403) {
        message.error("Access denied: You can only view your own assessments");
      } else {
        message.error("No Assessment have been created");
      }
      navigate("/assessments");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format: "excel" | "pdf") => {
    if (!assessmentId) return;

    try {
      const result = await apiService.exportAssessment(assessmentId, format);
      window.open(result.fileUrl, "_blank");
      message.success(`Assessment exported as ${format.toUpperCase()}`);
    } catch (error) {
      console.error("Error exporting assessment:", error);
      message.error("Failed to export assessment");
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "draft":
        return "orange";
      case "submitted":
        return "green";
      default:
        return "default";
    }
  };

  const getScoreColor = (score?: string) => {
    switch (score) {
      case "Adequate":
        return "green";
      case "Inadequate":
        return "red";
      default:
        return "default";
    }
  };

  // Questions where "No" is the correct/desired answer
  const INVERTED_QUESTIONS = ["C6", "M7", "M8"];

  const getResponseTagColor = (questionId: string, response: string | null) => {
    if (!response) return "default";
    const isInverted = INVERTED_QUESTIONS.includes(questionId);
    if (response === "Y") return isInverted ? "red" : "green";
    if (response === "N") return isInverted ? "green" : "red";
    if (response === "NA") return "blue";
    return "default";
  };

  const renderResponseCard = (response: any, index: number) => (
    <div className="v3-response-card" key={response.questionId || index}>
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
        {assessment!.assessmentType !== "CMA" && (
          <span
            className={`v3-response-tag ${
              getResponseTagColor(response.questionId, response.response) ===
              "green"
                ? "correct"
                : getResponseTagColor(
                      response.questionId,
                      response.response,
                    ) === "red"
                  ? "incorrect"
                  : "neutral"
            }`}
          >
            {response.response || "N/A"}
          </span>
        )}
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontWeight: 600,
              fontSize: 13,
              color: "var(--v3-gray-900)",
              marginBottom: 6,
            }}
          >
            {(() => {
              const liveQ = ALL_QUESTIONS.find(
                (q) => q.id === response.questionId,
              );
              return liveQ
                ? adaptQuestion(response.questionId, liveQ, t).question
                : response.question;
            })()}
          </div>
          {INVERTED_QUESTIONS.includes(response.questionId) && (
            <div
              style={{
                fontSize: 11,
                color: "var(--v3-gray-400)",
                marginBottom: 4,
              }}
            >
              (Expected answer: No)
            </div>
          )}
          {/* {response.guidance && (
            <div className="v3-q-guidance" style={{ marginBottom: 8 }}>
              <strong>Guidance:</strong> {response.guidance}
            </div>
          )} */}
          {response.aiAnswer && response.aiAnswer !== response.response && (
            <div
              style={{
                fontSize: 12,
                color: "var(--v3-gray-400)",
                marginTop: 4,
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <span
                style={{
                  fontSize: 10,
                  background: "var(--v3-gray-100)",
                  color: "var(--v3-gray-500)",
                  padding: "1px 6px",
                  borderRadius: 4,
                  fontWeight: 600,
                }}
              >
                AI suggested
              </span>
              <span>{response.aiAnswer}</span>
            </div>
          )}
          {response.comments && (
            <div
              style={{
                fontSize: 12,
                color: "var(--v3-gray-500)",
                marginTop: 4,
              }}
            >
              <strong>Comments:</strong> {response.comments}
            </div>
          )}
          {/* CMA fields */}
          {assessment!.assessmentType === "CMA" && response.initialReview && (
            <div
              style={{
                marginTop: 8,
                padding: 10,
                background: "var(--v3-gray-50)",
                borderRadius: "var(--v3-radius-md)",
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "var(--v3-gray-700)",
                  marginBottom: 4,
                }}
              >
                Initial Review:
              </div>
              <div style={{ fontSize: 13, color: "var(--v3-gray-700)" }}>
                {response.initialReview}
              </div>
            </div>
          )}
          {assessment!.assessmentType === "CMA" && response.proposedChanges && (
            <div
              style={{
                marginTop: 8,
                padding: 10,
                background: "var(--v3-gray-50)",
                borderRadius: "var(--v3-radius-md)",
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "var(--v3-gray-700)",
                  marginBottom: 4,
                }}
              >
                Proposed Changes:
              </div>
              <div style={{ fontSize: 13, color: "var(--v3-gray-700)" }}>
                {response.proposedChanges}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderControlResponses = () => {
    if (!assessment?.controlResponses) return null;

    let answeredCount = 0;
    let totalQuestions = assessment.controlResponses.length;

    if (assessment.assessmentType === "CMA") {
      answeredCount = assessment.controlResponses.filter(
        (r) =>
          r.initialReview &&
          r.initialReview.trim() !== "" &&
          r.proposedChanges &&
          r.proposedChanges.trim() !== "",
      ).length;
    } else {
      answeredCount = assessment.controlResponses.filter(
        (r) => r.response === "Y",
      ).length;
    }

    return (
      <div className="v3-detail-section">
        <div className="v3-detail-title">
          {assessment.assessmentType === "CMA"
            ? `${t.section_cma_control} Responses`
            : `${t.section_cda_questions} Responses`}
          <span
            style={{
              fontWeight: 400,
              fontSize: 13,
              color: "var(--v3-gray-400)",
              marginLeft: 8,
            }}
          >
            (
            {assessment.assessmentType === "CMA"
              ? "Questions Completed"
              : "Questions Answered Yes"}
            : {answeredCount} / {totalQuestions})
          </span>
        </div>
        <div style={{ marginBottom: 16 }}>
          <div className="v3-progress-bar-wrap">
            <div
              className="v3-progress-bar"
              style={{
                width: `${Math.round((answeredCount / totalQuestions) * 100)}%`,
                background:
                  assessment.assessmentType === "CMA"
                    ? answeredCount === totalQuestions
                      ? "var(--v3-success)"
                      : "var(--v3-danger)"
                    : answeredCount >= 18
                      ? "var(--v3-success)"
                      : "var(--v3-danger)",
              }}
            />
          </div>
        </div>
        {assessment.controlResponses.map((response: any, index: number) =>
          renderResponseCard(response, index),
        )}
      </div>
    );
  };

  const renderMonitoringResponses = () => {
    if (!assessment?.monitoringResponses) return null;

    let answeredCount = 0;
    let totalQuestions = assessment.monitoringResponses.length;

    if (assessment.assessmentType === "CMA") {
      answeredCount = assessment.monitoringResponses.filter(
        (r) =>
          r.initialReview &&
          r.initialReview.trim() !== "" &&
          r.proposedChanges &&
          r.proposedChanges.trim() !== "",
      ).length;
    } else {
      answeredCount = assessment.monitoringResponses.filter(
        (r) => r.response === "Y",
      ).length;
    }

    return (
      <div className="v3-detail-section">
        <div className="v3-detail-title">
          {assessment.assessmentType === "CMA"
            ? `${t.section_cma_monitoring} Responses`
            : `${t.section_mda_questions} Responses`}
          <span
            style={{
              fontWeight: 400,
              fontSize: 13,
              color: "var(--v3-gray-400)",
              marginLeft: 8,
            }}
          >
            (
            {assessment.assessmentType === "CMA"
              ? "Questions Completed"
              : "Questions Answered Yes"}
            : {answeredCount} / {totalQuestions})
          </span>
        </div>
        <div style={{ marginBottom: 16 }}>
          <div className="v3-progress-bar-wrap">
            <div
              className="v3-progress-bar"
              style={{
                width: `${Math.round((answeredCount / totalQuestions) * 100)}%`,
                background:
                  assessment.assessmentType === "CMA"
                    ? answeredCount === totalQuestions
                      ? "var(--v3-success)"
                      : "var(--v3-danger)"
                    : answeredCount >= 9
                      ? "var(--v3-success)"
                      : "var(--v3-danger)",
              }}
            />
          </div>
        </div>
        {assessment.monitoringResponses.map((response: any, index: number) =>
          renderResponseCard(response, index),
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: 50,
          color: "var(--v3-gray-500)",
          fontSize: 14,
        }}
      >
        Loading...
      </div>
    );
  }

  if (!assessment) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: 50,
          color: "var(--v3-gray-500)",
          fontSize: 14,
        }}
      >
        Assessment not found
      </div>
    );
  }

  // Calculate stats based on assessment type
  const controlResponses = assessment.controlResponses || [];
  const monitoringResponses = assessment.monitoringResponses || [];

  let controlStatValue = 0;
  let controlStatTotal =
    assessment.assessmentType === "CMDA" ? 18 : controlResponses.length;
  let monitoringStatValue = 0;
  let monitoringStatTotal =
    assessment.assessmentType === "CMDA" ? 9 : monitoringResponses.length;
  let monitoringAdditionalInfo: number | null = null;
  let controlStatus: "ADEQUATE" | "INADEQUATE" | "COMPLETED" | "INCOMPLETE" =
    "INCOMPLETE";
  let monitoringStatus: "ADEQUATE" | "INADEQUATE" | "COMPLETED" | "INCOMPLETE" =
    "INCOMPLETE";

  if (assessment.assessmentType === "CMA") {
    controlStatValue = controlResponses.filter(
      (r) =>
        r.initialReview &&
        r.initialReview.trim() &&
        r.proposedChanges &&
        r.proposedChanges.trim(),
    ).length;
    monitoringStatValue = monitoringResponses.filter(
      (r) =>
        r.initialReview &&
        r.initialReview.trim() &&
        r.proposedChanges &&
        r.proposedChanges.trim(),
    ).length;
    controlStatus =
      controlStatValue === controlStatTotal ? "COMPLETED" : "INCOMPLETE";
    monitoringStatus =
      monitoringStatValue === monitoringStatTotal ? "COMPLETED" : "INCOMPLETE";
  } else {
    const controlYesCount = controlResponses.filter(
      (r) => r.response === "Y",
    ).length;
    const controlYesExcludingC6 = controlResponses.filter(
      (r) => r.questionId !== "C6" && r.response === "Y",
    ).length;
    const controlAnsweredCount = controlResponses.filter(
      (r) => r.response === "Y" || r.response === "N" || r.response === "NA",
    ).length;
    const monitoringYesCount = monitoringResponses.filter(
      (r) =>
        r.questionId !== "M7" && r.questionId !== "M8" && r.response === "Y",
    ).length;
    const monitoringAnsweredCount = monitoringResponses.filter(
      (r) => r.response === "Y" || r.response === "N" || r.response === "NA",
    ).length;
    const c6Response = controlResponses.find((r) => r.questionId === "C6");
    const m7Response = monitoringResponses.find((r) => r.questionId === "M7");
    const m8Response = monitoringResponses.find((r) => r.questionId === "M8");

    controlStatValue = controlYesCount;
    monitoringStatValue = monitoringYesCount;

    const controlRequirement = controlResponses.length === 18 ? 17 : 16;
    const monitoringRequirement = 7;

    const controlAdequate =
      controlYesExcludingC6 >= controlRequirement &&
      c6Response?.response === "N";
    const monitoringAdequate =
      monitoringAnsweredCount === 9 &&
      monitoringYesCount >= monitoringRequirement &&
      m7Response?.response === "N" &&
      m8Response?.response === "N";

    controlStatus = controlAdequate ? "ADEQUATE" : "INADEQUATE";
    monitoringStatus = monitoringAdequate ? "ADEQUATE" : "INADEQUATE";

    controlStatTotal = 18;
    monitoringStatTotal = 7;
    monitoringAdditionalInfo = monitoringAnsweredCount;
  }

  const controlPercent =
    controlStatTotal > 0
      ? Math.round((controlStatValue / controlStatTotal) * 100)
      : 0;
  const monitoringPercent =
    monitoringStatTotal > 0
      ? Math.round((monitoringStatValue / monitoringStatTotal) * 100)
      : 0;

  return (
    <div>
      {/* Header bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 24,
        }}
      >
        <button
          className="back-btn"
          type="button"
          onClick={() => navigate("/assessments")}
        >
          ← Back to Assessments
        </button>
        <span className="v3-page-title">{assessment.assessmentName}</span>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          {isAssessor && assessment.status === "draft" && (
            <button
              className="v3-btn v3-btn-primary"
              type="button"
              onClick={() =>
                navigate(`/assessments/${assessment.assessmentId}/edit`)
              }
            >
              Edit Assessment
            </button>
          )}
          {assessment.status === "submitted" && (
            <>
              <button
                className="v3-btn v3-btn-outline"
                type="button"
                onClick={() => handleExport("excel")}
              >
                Export Excel
              </button>
              <button
                className="v3-btn v3-btn-outline"
                type="button"
                onClick={() => handleExport("pdf")}
              >
                Export PDF
              </button>
            </>
          )}
        </div>
      </div>

      {/* Assessment Details */}
      <div className="v3-detail-section">
        <div className="v3-detail-title">Assessment Details</div>
        <div className="v3-detail-row">
          <span className="v3-detail-label">Assessment Name</span>
          <span className="v3-detail-value">{assessment.assessmentName}</span>
        </div>
        <div className="v3-detail-row">
          <span className="v3-detail-label">Type</span>
          <span className="v3-detail-value">
            <span
              className={`v3-badge ${assessment.assessmentType === "CMA" ? "v3-badge-cma" : "v3-badge-cmda"}`}
            >
              {assessment.assessmentType}
            </span>
          </span>
        </div>
        <div className="v3-detail-row">
          <span className="v3-detail-label">Assessor</span>
          <span className="v3-detail-value">{assessment.assessorName}</span>
        </div>
        <div className="v3-detail-row">
          <span className="v3-detail-label">Status</span>
          <span className="v3-detail-value">
            <span
              className={`v3-badge ${assessment.status === "submitted" ? "v3-badge-submitted" : "v3-badge-draft"}`}
            >
              {assessment.status.toUpperCase()}
            </span>
          </span>
        </div>
        {assessment.overallScore && (
          <div className="v3-detail-row">
            <span className="v3-detail-label">Overall Score</span>
            <span className="v3-detail-value">
              <span
                className={`v3-badge ${assessment.overallScore === "Adequate" ? "v3-badge-adequate" : "v3-badge-inadequate"}`}
              >
                {assessment.overallScore === "Adequate"
                  ? t.rating_adequate
                  : t.rating_inadequate}
              </span>
            </span>
          </div>
        )}
        <div className="v3-detail-row">
          <span className="v3-detail-label">Created</span>
          <span className="v3-detail-value">
            {new Date(assessment.createdAt).toLocaleString()}
          </span>
        </div>
        <div className="v3-detail-row">
          <span className="v3-detail-label">Last Updated</span>
          <span className="v3-detail-value">
            {new Date(assessment.updatedAt).toLocaleString()}
          </span>
        </div>
      </div>

      {/* Summary */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 16,
          marginBottom: 16,
        }}
      >
        <div className="v3-rating-block">
          <div className="v3-rating-title">
            {assessment.assessmentType === "CMA"
              ? t.section_cma_control
              : t.section_cda_questions}
          </div>
          {/* <div className="v3-rating-detail">
            {controlStatValue} / {controlStatTotal}
          </div> */}
          <div className="v3-progress-bar-wrap">
            <div
              className="v3-progress-bar"
              style={{
                width: `${controlPercent}%`,
                background:
                  controlStatus === "ADEQUATE" || controlStatus === "COMPLETED"
                    ? "var(--v3-success)"
                    : "var(--v3-danger)",
              }}
            />
          </div>
          <div
            className={`v3-rating-status ${controlStatus === "ADEQUATE" || controlStatus === "COMPLETED" ? "adequate" : "inadequate"}`}
          >
            Status:{" "}
            {controlStatus === "ADEQUATE"
              ? t.rating_adequate.toUpperCase()
              : controlStatus === "INADEQUATE"
                ? t.rating_inadequate.toUpperCase()
                : controlStatus}
          </div>
        </div>
        <div className="v3-rating-block">
          <div className="v3-rating-title">
            {assessment.assessmentType === "CMA"
              ? t.section_cma_monitoring
              : t.section_mda_questions}
          </div>
          {/* <div className="v3-rating-detail">
            {monitoringStatValue} / {monitoringStatTotal}
          </div>
          {assessment.assessmentType === 'CMDA' && (
            <div style={{ fontSize: 12, color: 'var(--v3-gray-400)', marginBottom: 2 }}>
              All monitoring questions answered: {monitoringAdditionalInfo ?? 0}/9
            </div>
          )} */}
          <div className="v3-progress-bar-wrap">
            <div
              className="v3-progress-bar"
              style={{
                width: `${monitoringPercent}%`,
                background:
                  monitoringStatus === "ADEQUATE" ||
                  monitoringStatus === "COMPLETED"
                    ? "var(--v3-success)"
                    : "var(--v3-danger)",
              }}
            />
          </div>
          <div
            className={`v3-rating-status ${monitoringStatus === "ADEQUATE" || monitoringStatus === "COMPLETED" ? "adequate" : "inadequate"}`}
          >
            Status:{" "}
            {monitoringStatus === "ADEQUATE"
              ? t.rating_adequate.toUpperCase()
              : monitoringStatus === "INADEQUATE"
                ? t.rating_inadequate.toUpperCase()
                : monitoringStatus}
          </div>
        </div>
      </div>

      {/* Control Responses */}
      {renderControlResponses()}

      {/* Monitoring Responses */}
      {renderMonitoringResponses()}

      {/* CMA: Process & Risk Information */}
      {assessment.assessmentType === "CMA" && (
        <div className="v3-detail-section">
          <div className="v3-detail-title">Process & Risk Information</div>
          <div className="v3-detail-row">
            <span className="v3-detail-label">Process ID</span>
            <span className="v3-detail-value">
              {assessment.processId || "Not provided"}
            </span>
          </div>
          <div className="v3-detail-row">
            <span className="v3-detail-label">Process Description</span>
            <span className="v3-detail-value">
              {assessment.processDescription || "Not provided"}
            </span>
          </div>
          <div className="v3-detail-row">
            <span className="v3-detail-label">Activity Description</span>
            <span className="v3-detail-value">
              {assessment.activityDescription || "Not provided"}
            </span>
          </div>
          <div className="v3-detail-row">
            <span className="v3-detail-label">Risk IDs</span>
            <span className="v3-detail-value">
              {Array.isArray(assessment.riskIds)
                ? assessment.riskIds.join(", ")
                : assessment.riskIds || "Not provided"}
            </span>
          </div>
          <div className="v3-detail-row">
            <span className="v3-detail-label">Risk Consequences</span>
            <span className="v3-detail-value">
              {assessment.riskConsequences || "Not provided"}
            </span>
          </div>
          <div className="v3-detail-row">
            <span className="v3-detail-label">Process Initial Review</span>
            <span className="v3-detail-value">
              {assessment.processInitialReview || "Not provided"}
            </span>
          </div>
          <div className="v3-detail-row">
            <span className="v3-detail-label">Process Proposed Changes</span>
            <span className="v3-detail-value">
              {assessment.processProposedChanges || "Not provided"}
            </span>
          </div>
          <div className="v3-detail-row">
            <span className="v3-detail-label">Risk Initial Review</span>
            <span className="v3-detail-value">
              {assessment.riskInitialReview || "Not provided"}
            </span>
          </div>
          <div className="v3-detail-row">
            <span className="v3-detail-label">Risk Proposed Changes</span>
            <span className="v3-detail-value">
              {assessment.riskProposedChanges || "Not provided"}
            </span>
          </div>
        </div>
      )}

      {/* CMDA: CDA and MDA sections */}
      {assessment.assessmentType === "CMDA" && (
        <>
          <div className="v3-detail-section">
            <div className="v3-detail-title">{t.section_cda} (CDA)</div>
            {assessment.controlIssueId && (
              <div className="v3-detail-row">
                <span className="v3-detail-label">{t.issue_label} ID</span>
                <span className="v3-detail-value">
                  {assessment.controlIssueId}
                </span>
              </div>
            )}
            <div className="v3-detail-row">
              <span className="v3-detail-label">Design Completion Date</span>
              <span className="v3-detail-value">
                {assessment.designCompletionDate || "Not provided"}
              </span>
            </div>
            <div className="v3-detail-row">
              <span className="v3-detail-label">
                {t.facilitator_label} Name
              </span>
              <span className="v3-detail-value">
                {assessment.facilitatorName || "Not provided"}
              </span>
            </div>
            <div className="v3-detail-row">
              <span className="v3-detail-label">
                {t.facilitator_label} Employee ID
              </span>
              <span className="v3-detail-value">
                {assessment.facilitatorEmployeeId || "Not provided"}
              </span>
            </div>
            <div className="v3-detail-row">
              <span className="v3-detail-label">
                {t.facilitator_label} Knowledgeable
              </span>
              <span className="v3-detail-value">
                {assessment.facilitatorKnowledgeable || "Not provided"}
              </span>
            </div>
            <div className="v3-detail-row">
              <span className="v3-detail-label">Design Participants</span>
              <span className="v3-detail-value">
                {assessment.designParticipants || "Not provided"}
              </span>
            </div>
            <div className="v3-detail-row">
              <span className="v3-detail-label">
                {t.performer_label} Representative
              </span>
              <span className="v3-detail-value">
                {assessment.controlPerformerRepresentative || "Not provided"}
              </span>
            </div>
            <div className="v3-detail-row">
              <span className="v3-detail-label">Control ID</span>
              <span className="v3-detail-value">
                {assessment.controlId || "Not provided"}
              </span>
            </div>
            <div className="v3-detail-row">
              <span className="v3-detail-label">Control Description</span>
              <span className="v3-detail-value">
                {assessment.controlDescription || "Not provided"}
              </span>
            </div>
            {!t.req_hidden && (
              <div className="v3-detail-row">
                <span className="v3-detail-label">{t.req_field_label} IDs</span>
                <span className="v3-detail-value">
                  {assessment.regulationObligationIds || "Not provided"}
                </span>
              </div>
            )}
          </div>
          <div className="v3-detail-section">
            <div className="v3-detail-title">{t.section_mda} (MDA)</div>
            {assessment.monitoringIssueId && (
              <div className="v3-detail-row">
                <span className="v3-detail-label">Issue ID</span>
                <span className="v3-detail-value">
                  {assessment.monitoringIssueId}
                </span>
              </div>
            )}
            <div className="v3-detail-row">
              <span className="v3-detail-label">Monitoring Tool ID</span>
              <span className="v3-detail-value">
                {assessment.monitoringToolId || "Not provided"}
              </span>
            </div>
            <div className="v3-detail-row">
              <span className="v3-detail-label">
                Monitoring Tool Description
              </span>
              <span className="v3-detail-value">
                {assessment.monitoringToolDescription || "Not provided"}
              </span>
            </div>
          </div>
        </>
      )}

      {/* Team Members */}
      {assessment.teamMembers &&
        typeof assessment.teamMembers === "string" &&
        assessment.teamMembers.trim() && (
          <div className="v3-rating-block">
            <div className="v3-rating-title">Team Members</div>
            <div style={{ fontSize: 13, color: "var(--v3-gray-700)" }}>
              {assessment.teamMembers}
            </div>
          </div>
        )}

      {/* Final Assessment Comment */}
      {assessment.finalAssessmentComment && (
        <div className="v3-rating-block">
          <div className="v3-rating-title">Final Assessment Comment</div>
          <div style={{ fontSize: 13, color: "var(--v3-gray-700)" }}>
            {assessment.finalAssessmentComment}
          </div>
        </div>
      )}
    </div>
  );
};

export default AssessmentDetail;
